import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import {
  FilesRailCanvasContextBody,
  FilesRailCanvasFilesList,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailContextSection";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import { useRailScopeOsDrop } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useRailScopeOsDrop";
import {
  RAIL_LIST_ITEM_ATTR,
  RAIL_SELECTED_ATTR,
  useRailRowSelection,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { WorkspacesRailDraggableCanvasRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailDraggableCanvasRow";
import { useCanvasNodesMirrorSynced } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import type { Canvas } from "@cohesive/api-client";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";

/**
 * The canvas node as a file drop target (add a file into this canvas — copy into
 * context). Wraps the whole node (header + expanded files), so a file can be
 * dropped anywhere on it. When the canvas is open it uses the `canvas-scope`
 * surface so the dispatcher places a node on the board; otherwise the
 * `rail-canvas-row` surface just copies the file into the canvas's scope.
 * OS file drops upload straight into the canvas's scope.
 *
 * It is a FILE-only zone, so it stays **disabled** while dragging a canvas or
 * chat — a file zone must never intercept a canvas/chat drag meant for the
 * section or a folder (dnd-kit skips disabled droppables in collision).
 *
 * Also the contiguous-selection list-item host: adjacent canvas nodes are
 * drop-zone siblings, not row siblings.
 */
function CanvasRowDropZone({
  canvasId,
  canvasOpen,
  section = "canonical",
  children,
}: {
  canvasId: string;
  canvasOpen: boolean;
  section?: RailSelectionSource;
  children: React.ReactNode;
}) {
  const { active } = useDndContext();
  const activeIsFiles =
    !!active && asDraggableData(active.data.current)?.kind === "files";

  const { selected } = useRailRowSelection(
    canvasId,
    section,
    false,
    "canvas",
    false,
  );

  const surface = canvasOpen ? "canvas-scope" : "rail-canvas-row";
  const dropId = droppableId.railCanvasRow(canvasId);
  const droppableData = useMemo(
    (): DroppableData => ({
      surface,
      destination: destinationFor(surface, { canvasId }),
    }),
    [surface, canvasId],
  );
  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
    disabled: !activeIsFiles,
  });
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  const handleOsDrop = useRailScopeOsDrop({ canvasId });
  const osActiveDropId = useRailOsDropHighlightId();
  const osOver = osActiveDropId === dropId;

  return (
    <RailOsFileDropZone
      ref={droppable.setNodeRef}
      dropId={dropId}
      onDrop={handleOsDrop}
      className={cnTransferDropTargetSurface({
        isOver: isOver || osOver,
        disallowed,
      })}
      {...{ [RAIL_LIST_ITEM_ATTR]: "" }}
      {...(selected ? { [RAIL_SELECTED_ATTR]: "" } : {})}
    >
      {children}
    </RailOsFileDropZone>
  );
}

/**
 * A canvas inside the workspace containment tree — the §5.1 node that expands to
 * reveal its files. For the canvas the user currently has open, the canvas's
 * live node graph is mirrored into this rail, so we show the rich view (section
 * folders + visible/hidden state), exactly like the legacy files rail. For any
 * other canvas there's no node graph to read (it lives only in the open
 * Liveblocks room), so we fall back to a flat, backend-driven file list. The
 * header is the normal draggable canvas row (title click selects, chevron
 * expands, double click opens the canvas); the leading icon doubles as the
 * disclosure toggle. Children mount lazily on expand.
 */
export function WorkspacesRailCanvasNode({
  canvas,
  dragNamespace,
  section,
  active,
  onRename,
  onMove,
  onShare,
  onDelete,
}: {
  canvas: Canvas;
  dragNamespace: string;
  /** Rail section this node lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  active?: boolean;
  onRename: (canvas: Canvas) => void;
  onMove?: (canvas: Canvas) => void;
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
}) {
  const [expanded, setExpanded] = useRailExpansion(
    `node:${dragNamespace}:${canvas.id}`,
  );

  // True only for the canvas the user currently has open (its node graph is
  // mirrored into the rail). Drives the rich-vs-flat choice below.
  const canvasOpen = useCanvasNodesMirrorSynced(canvas.id);

  return (
    <CanvasRowDropZone
      canvasId={canvas.id}
      canvasOpen={canvasOpen}
      section={section}
    >
      <div className="flex flex-col">
        <WorkspacesRailDraggableCanvasRow
          canvas={canvas}
          dragNamespace={dragNamespace}
          section={section}
          active={active}
          onRename={onRename}
          onMove={onMove}
          onShare={onShare}
          onDelete={onDelete}
          expanded={expanded}
          onToggleExpanded={() => setExpanded(!expanded)}
        />
        {expanded && (
          // pl-2: the rail's uniform 8px step per level, matching the
          // workspace→sections indent. The file rows carry a phantom chevron
          // spacer (same internal 28px icon offset as this row's chevron +
          // Frame icon), so their icons land exactly 8px deeper than the
          // canvas icon. No gap — selection highlight bands must stay
          // contiguous.
          <div className="flex flex-col pl-2">
            {canvasOpen ? (
              <FilesRailCanvasContextBody
                canvasId={canvas.id}
                withChevronSpacer
              />
            ) : (
              <FilesRailCanvasFilesList
                canvasId={canvas.id}
                withChevronSpacer
              />
            )}
          </div>
        )}
      </div>
    </CanvasRowDropZone>
  );
}
